import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import React, { useEffect, useState } from "react";
import LyricRequirements from "./components/requirements/lyric-requirements";
import Tabs from "./components/tabs";
import TrackDetails from "./components/track-details";
import { albumCover } from "constants/placeholderAlbumCover";
import LyricsInput from "./components/LyricsInput";
import LyricsSync from "./components/LyricsSync";
import audio from "constants/lyric-song-2.mp3";
import SyncRequirements from "./components/requirements/sync-requirements";
import { useParams } from "react-router-dom";
import TrackCredits from "./components/credits";
import { GetTrackData } from "services/getTrack";
import { ReleaseContainerWithUrls, SongWithUrls } from "types/response-type";
import Toast from "components/toast";
import Spinner from "components/spinner";
import api from "services/axiosInstance";

const TrackLyrics = () => {
  const { id } = useParams();

  const url = new URL(window.location.href);
  const queryParams = new URLSearchParams(url.search);

  const tab = queryParams.get("tab");
  const type = queryParams.get("type");

  const [activeTab, setActiveTab] = useState(tab || "plain");

  const [lyrics, setLyrics] = useState("");
  const [savingLyrics, setSavingLyrics] = useState(false);
  const [savedLyrics, setSavedLyrics] = useState(false);
  const [syncedLyrics, setSyncedLyrics] = useState<
    { text: string; timestamp: number }[]
  >([]);
  const [restartSync, setRestartSync] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalItem, setActiveModalItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [track, setTrack] = useState<SongWithUrls>();
  const [lyricsId, setLyricsId] = useState("");

  const GetData = async () => {
    setLoading(true);
    try {
      const response = await GetTrackData(id || "", "SINGLE");
      if (response) {
        const { track } = response;

        setTrack(track as SongWithUrls);
      }

      ///get posted lyrics and set state if available
      const { data } = await api.get(`/lyrics/song/${id}`);

      if (!data) return;

      const Data = data.data;

      if (Data?.basicLyrics === "" || !Data.basicLyrics) {
        setActiveTab("plain");
      }

      if (Data?.synchronizedLyrics.length === 0) {
        setRestartSync(true);
      }

      if (Data?.basicLyrics) {
        setLyrics(data.data?.basicLyrics);
        if (Data.synchronizedLyrics) {
          setSyncedLyrics(data.data.synchronizedLyrics);
          setSavedLyrics(true);
        }
        setLyricsId(data.data.id);
      }
    } catch (error) {
      // Toast({ error });
    } finally {
      setLoading(false);
    }
  };

  const saveLyrics = async () => {
    setSavedLyrics(true);

    await PostBasicLyrics();
  };

  const SaveSyncedLyrics = async (
    syncedLyrics: { text: string; timestamp: number }[]
  ) => {
    setSyncedLyrics(syncedLyrics);

    setIsModalOpen(true);
    await PostSyncedLyrics(syncedLyrics);
  };

  const PostBasicLyrics = async () => {
    setSavingLyrics(true);
    try {
      if (lyricsId) {
        await api.put(`/lyrics/${lyricsId}`, {
          basicLyrics: lyrics
        });
      } else {
        const { data } = await api.post("/lyrics", {
          songId: id,
          basicLyrics: lyrics
        });
        setLyricsId(data.data.id);
      }

      Toast({ message: "Lyrics saved successfully", type: "success" });

      setRestartSync(true);
      setActiveTab("sync");
    } catch (error) {
      Toast({ error });
    } finally {
      setSavingLyrics(false);
    }
  };

  const PostSyncedLyrics = async (
    syncedLyrics: { text: string; timestamp: number }[]
  ) => {
    setSavingLyrics(true);
    try {
      const response = await api.put(`/lyrics/${lyricsId}`, {
        synchronizedLyrics: syncedLyrics
      });

      if (response.status === 200) {
        Toast({ message: "Synced lyrics saved successfully" });
      }

      setActiveModalItem("save");
    } catch (error) {
      Toast({ error });
    } finally {
      setSavingLyrics(false);
    }
  };

  useEffect(() => {
    GetData();
  }, []);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  return (
    <DashboardLayout>
      {loading ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Spinner />
        </div>
      ) : track ? (
        <>
          <PageLinks
            from={{
              name: track?.title || "",
              link: `/overview/discography/track/${id}?type=${type}`
            }}
            current={
              activeTab === "plain"
                ? "Plain Lyrics"
                : activeTab === "sync"
                ? "Synced Lyrics"
                : "Credits"
            }
          />
          <Tabs
            isactive={activeTab}
            setTab={(value) => setActiveTab(value)}
            savedLyrics={savedLyrics}
          />

          {activeTab === "plain" ? (
            <LyricRequirements />
          ) : activeTab === "sync" ? (
            <SyncRequirements />
          ) : (
            ""
          )}

          <TrackDetails
            coverImage={track?.coverArtPath || ""}
            name={track?.title || ""}
            artists={track?.primaryArtist.name || ""}
            activeTab={activeTab}
            openModal={() => {
              setIsModalOpen(true);
              setActiveModalItem("restart");
            }}
          />
          {activeTab === "plain" && (
            <LyricsInput
              value={lyrics}
              setValue={(value) => setLyrics(value)}
              saveLyrics={saveLyrics}
              loading={savingLyrics}
            />
          )}
          {activeTab === "sync" && lyrics !== "" && (
            <LyricsSync
              isRestart={restartSync}
              title={track?.title || ""}
              lyrics={lyrics}
              audio={track?.masterTrackPath || ""}
              isModalOpen={isModalOpen}
              closeModal={() => setIsModalOpen(false)}
              modalItem={activeModalItem}
              save={(syncedLyrics) => SaveSyncedLyrics(syncedLyrics)}
              loading={savingLyrics}
              syncedLyrics={syncedLyrics}
            />
          )}
          {activeTab === "credits" && (
            <TrackCredits title={track?.title || ""} trackId={id || ""} />
          )}
        </>
      ) : (
        <></>
      )}
    </DashboardLayout>
  );
};

export default TrackLyrics;
